import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useTenant } from "@/providers/session";
import {
  type ApolloError,
  type FetchResult,
  gql,
  useMutation,
} from "@apollo/client";

type UpdateBankAccountInput = {
  bankAccountId: string;
  defaultAccount: boolean;
};

export const useUpdateBankAccount = (): [
  (
    data: UpdateBankAccountInput,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult>,
  {
    data: UpdateBankAccountInput;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [updateBankAccount, { data, loading, error }] = useMutation(
    UPDATE_BANK_ACCOUNT_MUTATION,
  );

  const handleUpdateBankAccount = async (
    data: UpdateBankAccountInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await updateBankAccount({
        variables: {
          ...data,
          tenantId: tenantId,
        },
      });

      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return response;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw error;
    }
  };

  return [handleUpdateBankAccount, { data, loading, error }];
};

export const UPDATE_BANK_ACCOUNT_MUTATION = gql`
  mutation UpdateBankAccount(
    $bankAccountId: String!
    $defaultAccount: Boolean!
    $tenantId: String!
  ) {
    updateBankAccount(
      bankAccountId: $bankAccountId
      defaultAccount: $defaultAccount
      tenantId: $tenantId
    ) {
      bankAccountId
      defaultAccount
    }
  }
`;
